import { OpenMeteoResponse, WeatherData, WeatherCondition } from "./types";
import { getCachedWeather, setCachedWeather } from "./cache";

// Hamilton Township, NJ coordinates (80 Sculptors Way, Hamilton Township, NJ 08619)
const LATITUDE = 40.2368;
const LONGITUDE = -74.7189;
const CACHE_TTL = 20 * 60 * 1000; // 20 minutes

// Demo/fallback data
const DEMO_WEATHER: WeatherData = {
  temp: 72,
  condition: "Clear",
  icon: "☀️",
};

// WMO Weather Code to condition mapping
const WMO_WEATHER_CODES: Record<number, WeatherCondition> = {
  0: { description: "Clear", iconDay: "☀️", iconNight: "🌙" },
  1: { description: "Mainly Clear", iconDay: "☀️", iconNight: "🌙" },
  2: { description: "Partly Cloudy", iconDay: "⛅", iconNight: "☁️" },
  3: { description: "Overcast", iconDay: "☁️", iconNight: "☁️" },
  45: { description: "Foggy", iconDay: "🌫️", iconNight: "🌫️" },
  48: { description: "Rime Fog", iconDay: "🌫️", iconNight: "🌫️" },
  51: { description: "Light Drizzle", iconDay: "🌦️", iconNight: "🌧️" },
  53: { description: "Drizzle", iconDay: "🌦️", iconNight: "🌧️" },
  55: { description: "Dense Drizzle", iconDay: "🌧️", iconNight: "🌧️" },
  56: { description: "Freezing Drizzle", iconDay: "🌧️", iconNight: "🌧️" },
  57: { description: "Freezing Drizzle", iconDay: "🌧️", iconNight: "🌧️" },
  61: { description: "Light Rain", iconDay: "🌦️", iconNight: "🌧️" },
  63: { description: "Rain", iconDay: "🌧️", iconNight: "🌧️" },
  65: { description: "Heavy Rain", iconDay: "🌧️", iconNight: "🌧️" },
  66: { description: "Freezing Rain", iconDay: "🌧️", iconNight: "🌧️" },
  67: { description: "Freezing Rain", iconDay: "🌧️", iconNight: "🌧️" },
  71: { description: "Light Snow", iconDay: "🌨️", iconNight: "🌨️" },
  73: { description: "Snow", iconDay: "❄️", iconNight: "❄️" },
  75: { description: "Heavy Snow", iconDay: "❄️", iconNight: "❄️" },
  77: { description: "Snow Grains", iconDay: "❄️", iconNight: "❄️" },
  80: { description: "Light Showers", iconDay: "🌦️", iconNight: "🌧️" },
  81: { description: "Showers", iconDay: "🌧️", iconNight: "🌧️" },
  82: { description: "Heavy Showers", iconDay: "🌧️", iconNight: "🌧️" },
  85: { description: "Snow Showers", iconDay: "🌨️", iconNight: "🌨️" },
  86: { description: "Heavy Snow Showers", iconDay: "❄️", iconNight: "❄️" },
  95: { description: "Thunderstorm", iconDay: "⛈️", iconNight: "⛈️" },
  96: { description: "Thunderstorm with Hail", iconDay: "⛈️", iconNight: "⛈️" },
  99: { description: "Thunderstorm with Hail", iconDay: "⛈️", iconNight: "⛈️" },
};

/**
 * Get weather condition details from WMO code
 */
function getWeatherCondition(
  code: number,
  isDay: boolean,
): { condition: string; icon: string } {
  const weatherInfo = WMO_WEATHER_CODES[code];

  if (!weatherInfo) {
    return {
      condition: "Clear",
      icon: isDay ? "☀️" : "🌙",
    };
  }

  return {
    condition: weatherInfo.description,
    icon: isDay ? weatherInfo.iconDay : weatherInfo.iconNight,
  };
}

/**
 * Transform Open-Meteo response to internal WeatherData format
 */
function transformResponse(response: OpenMeteoResponse): WeatherData {
  const { current } = response;
  const isDay = current.is_day === 1;
  const { condition, icon } = getWeatherCondition(current.weather_code, isDay);

  return {
    temp: Math.round(current.temperature_2m),
    condition,
    icon,
  };
}

/**
 * Fetch weather data from Open-Meteo API
 * Uses caching to reduce API calls
 */
export async function fetchWeather(): Promise<WeatherData> {
  // Check cache first
  const cachedData = getCachedWeather();
  if (cachedData) {
    return cachedData;
  }

  try {
    const url = new URL("https://api.open-meteo.com/v1/forecast");
    url.searchParams.set("latitude", LATITUDE.toString());
    url.searchParams.set("longitude", LONGITUDE.toString());
    url.searchParams.set("current", "temperature_2m,weather_code,is_day");
    url.searchParams.set("temperature_unit", "fahrenheit");
    url.searchParams.set("timezone", "America/New_York");

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Weather API error: ${response.status}`);
    }

    const data: OpenMeteoResponse = await response.json();
    const weatherData = transformResponse(data);

    // Cache the result
    setCachedWeather(weatherData, CACHE_TTL);

    return weatherData;
  } catch (error) {
    console.error("Error fetching weather from Open-Meteo:", error);
    return DEMO_WEATHER;
  }
}

/**
 * Get demo weather data (for testing or fallback)
 */
export function getDemoWeather(): WeatherData {
  return DEMO_WEATHER;
}
